/**
 * WebRTC peer connection management
 */

export interface RTCConfig {
  iceServers: RTCIceServer[];
}

const DEFAULT_CONFIG: RTCConfig = {
  iceServers: [
    { urls: 'stun:dnull.com:3478' },
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    {
      urls: 'turn:dnull.com:3478',
      username: 'p2psocial',
      credential: 'ChangeThisPassword123'
    },
  ],
};

export interface DataChannelConfig {
  ordered?: boolean;
  maxRetransmits?: number;
  label: string;
}

export type ConnectionState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';

export interface PeerConnectionEvents {
  onStateChange?: (state: ConnectionState) => void;
  onIceCandidate?: (candidate: RTCIceCandidate) => void;
  onDataChannel?: (channel: RTCDataChannel) => void;
  onChannelOpen?: (label: string) => void;
  onMessage?: (channel: string, data: ArrayBuffer | string) => void;
  onTrack?: (track: MediaStreamTrack, streams: readonly MediaStream[]) => void;
}

/**
 * Manages a single WebRTC peer connection
 */
export class PeerConnection {
  readonly peerId: string;
  private pc: RTCPeerConnection;
  private channels: Map<string, RTCDataChannel> = new Map();
  private events: PeerConnectionEvents;
  private _state: ConnectionState = 'new';
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private hasRemoteDescription: boolean = false;

  constructor(peerId: string, config: RTCConfig = DEFAULT_CONFIG, events: PeerConnectionEvents = {}) {
    this.peerId = peerId;
    this.events = events;
    this.pc = new RTCPeerConnection(config);

    this.setupEventHandlers();
  }

  get state(): ConnectionState {
    return this._state;
  }

  /**
   * Get the WebRTC signaling state
   */
  get signalingState(): RTCSignalingState {
    return this.pc.signalingState;
  }

  private setState(state: ConnectionState) {
    this._state = state;
    this.events.onStateChange?.(state);
  }

  private setupEventHandlers() {
    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        this.events.onIceCandidate?.(event.candidate);
      }
    };

    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      switch (state) {
        case 'connecting':
          this.setState('connecting');
          break;
        case 'connected':
          this.setState('connected');
          break;
        case 'disconnected':
          this.setState('disconnected');
          break;
        case 'failed':
          this.setState('failed');
          break;
        case 'closed':
          this.setState('closed');
          break;
      }
    };

    this.pc.ondatachannel = (event) => {
      const channel = event.channel;
      this.setupDataChannel(channel);
      this.events.onDataChannel?.(channel);
    };

    this.pc.ontrack = (event) => {
      this.events.onTrack?.(event.track, event.streams);
    };
  }

  private setupDataChannel(channel: RTCDataChannel) {
    this.channels.set(channel.label, channel);

    channel.onopen = () => {
      this.events.onChannelOpen?.(channel.label);
    };

    channel.onmessage = (event) => {
      this.events.onMessage?.(channel.label, event.data);
    };

    channel.onclose = () => {
      this.channels.delete(channel.label);
    };
  }

  /**
   * Create a data channel (initiator side)
   */
  createDataChannel(config: DataChannelConfig): RTCDataChannel {
    const channel = this.pc.createDataChannel(config.label, {
      ordered: config.ordered ?? true,
      maxRetransmits: config.maxRetransmits,
    });
    this.setupDataChannel(channel);
    return channel;
  }

  /**
   * Get a data channel by label
   */
  getChannel(label: string): RTCDataChannel | undefined {
    return this.channels.get(label);
  }

  /**
   * Add a media track (for video/audio calls)
   */
  addTrack(track: MediaStreamTrack, stream: MediaStream): RTCRtpSender {
    return this.pc.addTrack(track, stream);
  }

  /**
   * Remove a media track
   */
  removeTrack(sender: RTCRtpSender): void {
    this.pc.removeTrack(sender);
  }

  /**
   * Get all RTP senders (for managing tracks)
   */
  getSenders(): RTCRtpSender[] {
    return this.pc.getSenders();
  }

  /**
   * Get all RTP receivers (for managing incoming tracks)
   */
  getReceivers(): RTCRtpReceiver[] {
    return this.pc.getReceivers();
  }

  /**
   * Create an offer (initiator)
   */
  async createOffer(): Promise<RTCSessionDescriptionInit> {
    this.setState('connecting');
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return offer;
  }

  /**
   * Rollback a pending offer (for glare resolution)
   */
  async rollback(): Promise<void> {
    if (this.pc.signalingState === 'have-local-offer') {
      await this.pc.setLocalDescription({ type: 'rollback' });
    }
  }

  /**
   * Create an answer (responder)
   */
  async createAnswer(offer: RTCSessionDescriptionInit): Promise<RTCSessionDescriptionInit> {
    this.setState('connecting');
    // Pass offer directly - wrapping in RTCSessionDescription breaks werift (Node.js)
    // Browser and werift both accept plain objects for setRemoteDescription
    await this.pc.setRemoteDescription(offer as RTCSessionDescription);
    this.hasRemoteDescription = true;
    await this.flushPendingCandidates();
    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return answer;
  }

  /**
   * Set remote answer (initiator)
   */
  async setRemoteAnswer(answer: RTCSessionDescriptionInit): Promise<void> {
    // Pass answer directly - wrapping breaks werift (Node.js)
    await this.pc.setRemoteDescription(answer as RTCSessionDescription);
    this.hasRemoteDescription = true;
    await this.flushPendingCandidates();
  }

  /**
   * Add an ICE candidate
   */
  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.hasRemoteDescription) {
      // Buffer candidate until remote description is set
      this.pendingCandidates.push(candidate);
      return;
    }
    // Pass candidate directly - wrapping breaks werift (Node.js)
    await this.pc.addIceCandidate(candidate as RTCIceCandidate);
  }

  /**
   * Flush buffered ICE candidates
   */
  private async flushPendingCandidates(): Promise<void> {
    for (const candidate of this.pendingCandidates) {
      try {
        await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (e) {
        console.warn('[WebRTC] Failed to add buffered ICE candidate:', e);
      }
    }
    this.pendingCandidates = [];
  }

  /**
   * Send data on a channel
   */
  send(label: string, data: string | ArrayBuffer): boolean {
    const channel = this.channels.get(label);
    if (channel && channel.readyState === 'open') {
      channel.send(data as any);
      return true;
    }
    return false;
  }

  /**
   * Close the connection
   */
  close() {
    for (const channel of this.channels.values()) {
      channel.close();
    }
    this.channels.clear();
    this.pc.close();
    this.setState('closed');
  }
}
